import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import { isValidEmail } from "../../../utils/helpers";
import httpRequest from "../../../ajax";
import WBtext from "../../../assets/icons/WBtext.svg";
import "./index.scss";

const ENABLE_LEAD_MODAL = false;

const FEATURE_ROWS = [
  { label: "Listed on Workbrook jobs feed", tiers: [true, true, true] },
  { label: "AI match score for applicants", tiers: [true, true, true] },
  { label: "Applicant management dashboard", tiers: [true, true, true] },
  { label: "Verified listing goes live instantly", tiers: [true, true, true] },
  { label: "Priority placement in feed", tiers: [false, true, true] },
  { label: "Distributed to partner platforms", tiers: [false, true, true] },
  { label: "Candidate ranking and summary", tiers: [false, true, true] },
  { label: "AI talent discovery", tiers: [false, false, true] },
  { label: "Referral reward pool", tiers: [false, false, true] },
  {
    label: "Cost",
    tiers: ["Free", "Pay per view", "10% fee on hire"],
    isNote: true,
  },
];

const TIER_META = [
  { name: "Free", sub: "Entry-level roles", icon: "🌱" },
  { name: "Pay Per View", sub: "Boost visibility", icon: "⚡" },
  { name: "Full Placement", sub: "Executive & urgent hires", recommended: true, icon: "🚀" },
];


const STEPS = [
  {
    title: "Sign up",
    copy: "Create your free employer account in under 2 minutes.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="9" cy="8" r="3.25" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3.5 19c.6-3.2 3-5 5.5-5s4.9 1.8 5.5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M17.5 8v5M15 10.5h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: "Post a job",
    copy: "Fill in role details and select your tier.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 3.5h8l4 4V20a.5.5 0 0 1-.5.5h-11A.5.5 0 0 1 6 20V4a.5.5 0 0 1 .5-.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M14 3.5V8h4" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M9 13h6M9 16.5h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: "Get verified",
    copy: "Verify your work email and your job goes live instantly.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 3.5 5 6v5.2c0 4.2 2.9 7.6 7 8.8 4.1-1.2 7-4.6 7-8.8V6l-7-2.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="m9 12 2.1 2.1L15.5 9.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Track applications",
    copy: "Review AI-scored applicants in your hiring dashboard.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 20V4M4 20h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M8 16v-3.5M12.5 16V8M17 16v-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  },
];

const MOCK_CANDIDATES = [
  { name: "A. Okafor", role: "5 yrs · Product Design", score: 96 },
  { name: "J. Bello", role: "7 yrs · Product Design", score: 91 },
  { name: "T. Adeyemi", role: "4 yrs · UX / Research", score: 84 },
];

const STATS = [
  { value: "10k+", label: "Active employers" },
  { value: "48h", label: "Avg. time to hire" },
  { value: "94%", label: "Satisfaction rate" },
];

const PostAJobPreview = () => {
  const history = useHistory();
  const { isAuthenticated } = useSelector((store) => store.auth);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadEmail, setLeadEmail] = useState("");
  const [leadEmailError, setLeadEmailError] = useState("");
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadSuccess, setLeadSuccess] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTierCard, setActiveTierCard] = useState(1);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    const timer = setTimeout(() => {
      if (ENABLE_LEAD_MODAL && !sessionStorage.getItem("intent")) setShowLeadModal(true);
    }, 15000);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timer);
    };
  }, []);

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadEmailError("");
    setLeadSuccess(false);
    if (!isValidEmail(leadEmail)) {
      setLeadEmailError("Please enter a valid email address");
      return;
    }
    setLeadLoading(true);
    try {
      await httpRequest("post", "/waitlist", { email: leadEmail, intent: "post-job" });
      setLeadSuccess(true);
    } catch (err) {
      setLeadEmailError("Failed to submit. Please try again.");
    } finally {
      setLeadLoading(false);
    }
  };

  const handlePostAJobClick = () => {
    if (isAuthenticated) {
      history.push("/my-job-posts/jobs-posted");
      setTimeout(() => window.dispatchEvent(new Event("open-template-selector")), 150);
    } else {
      localStorage.setItem("accountType", "business");
      sessionStorage.setItem("intent", "post-job");
      history.push("/signup");
    }
  };

  const handleSignInClick = () => history.push("/signin");

  return (
    <div className="post-job-preview">
      {/* Nav */}
      <nav className={"preview-nav" + (isScrolled ? " scrolled" : "") + (mobileMenuOpen ? " menu-open" : "")}>
        <div className="nav-container">
          <img className="pl-2 nav-logo" src={WBtext} alt="workbrook logo" />
          <div className="nav-actions">
            {isAuthenticated ? (
              <CustomButton text="Post a Job" backgroundColor="#3A6ED4" color="#fff" borderColor="transparent" additionalClass="nav-cta-btn" handleClick={handlePostAJobClick} />
            ) : (
              <>
                <button className="sign-in-btn" onClick={handleSignInClick}>Sign in</button>
                <CustomButton text="Post a Job" backgroundColor="#3A6ED4" color="#fff" borderColor="transparent" additionalClass="nav-cta-btn" handleClick={handlePostAJobClick} />
              </>
            )}
          </div>
          <button className="nav-hamburger" aria-label={mobileMenuOpen ? "Close menu" : "Open menu"} aria-expanded={mobileMenuOpen} onClick={() => setMobileMenuOpen((v) => !v)}>
            <span /><span /><span />
          </button>
        </div>
        <div className={"nav-mobile-menu" + (mobileMenuOpen ? " open" : "")}>
          {!isAuthenticated && (
            <button className="mobile-sign-in-btn" onClick={() => { setMobileMenuOpen(false); handleSignInClick(); }}>Sign in</button>
          )}
          <button className="mobile-cta-btn" onClick={() => { setMobileMenuOpen(false); handlePostAJobClick(); }}>
            Post a Job — it's free
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="preview-hero">
        <div className="hero-blob hero-blob-1" aria-hidden="true" />
        <div className="hero-blob hero-blob-2" aria-hidden="true" />
        <div className="hero-inner">
          <div className="hero-copy">
            <p className="hero-kicker"><span className="kicker-dot" aria-hidden="true" />For employers</p>
            <h1 className="hero-title">
              <span className="title-line">Hire faster.</span>
              <span className="title-line">Reach qualified candidates.</span>
              <span className="title-line title-line-accent">Save hours every week.</span>
            </h1>
            <p className="hero-subtitle">Post the role once. Workbrook ranks every applicant against it, so you spend your time meeting the right five people — not skimming three hundred resumes.</p>
            <div className="hero-actions">
              <button className="hero-cta-btn" onClick={handlePostAJobClick}>
                Post a Job — it's free
                <svg className="btn-arrow" viewBox="0 0 20 20" fill="none"><path d="M4 10h12M11 5l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
              {!isAuthenticated && (
                <button className="hero-secondary-link" onClick={handleSignInClick}>Already have an account? Sign in →</button>
              )}
            </div>
            <p className="hero-proof">Free to post · no card required on Tier 1</p>
            <div className="hero-stats" aria-label="Platform statistics">
              {STATS.map((s) => (
                <div className="hero-stat" key={s.label}>
                  <span className="hero-stat-value">{s.value}</span>
                  <span className="hero-stat-label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="hero-visual" aria-hidden="true">
            <div className="mock-panel">
              <div className="mock-panel-head">
                <div>
                  <span className="mock-panel-role">Senior Product Designer</span>
                  <span className="mock-panel-company">Lagos · Remote</span>
                </div>
                <span className="mock-panel-tag"><span className="live-dot" />Live</span>
              </div>
              <div className="mock-panel-sub">Ranked by AI match score</div>
              <ul className="mock-panel-list">
                {MOCK_CANDIDATES.map((c, i) => (
                  <li className="mock-row" key={c.name} style={{ animationDelay: `${i * 0.15}s` }}>
                    <div className="mock-row-avatar">{c.name.charAt(0)}</div>
                    <div className="mock-row-info">
                      <span className="mock-row-name">{c.name}</span>
                      <span className="mock-row-role">{c.role}</span>
                    </div>
                    <div className="mock-row-score">
                      <div className="mock-score-track">
                        <div className="mock-score-fill" style={{ width: `${c.score}%` }} />
                      </div>
                      <span className="mock-score-value">{c.score}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mock-panel-footer">
                <span className="mock-footer-applicants">24 applicants total</span>
                <button className="mock-footer-btn">View all</button>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Social proof strip */}
      <div className="proof-strip">
        <div className="proof-inner">
          <div className="proof-label-block">
            <span className="proof-label-main">Trusted by teams hiring</span>
            <span className="proof-label-sub">across industries</span>
          </div>
          <div className="proof-divider" aria-hidden="true" />
          <div className="proof-industries">
            {["Financial Services", "Manufacturing", "Foods & Beverages", "Construction", "Fintech", "Maritime"].map((name) => (
              <span className="proof-industry" key={name}>{name}</span>
            ))}
          </div>
        </div>
      </div>



      {/* Tiers */}
      <section className="preview-tiers" id="tiers">
        <div className="tiers-header">
          <span className="section-kicker">Pricing tiers</span>
          <h2>Choose the right tier for your role</h2>
          <p>Every job on Workbrook is posted under a tier that determines visibility, reach, and referral eligibility.</p>
        </div>
        <div className="tiers-cards-mobile">
          <div className="tier-tab-nav" role="tablist">
            {TIER_META.map((tier, i) => (
              <button key={tier.name} className={"tier-tab" + (activeTierCard === i ? " active" : "")} role="tab" aria-selected={activeTierCard === i} onClick={() => setActiveTierCard(i)}>
                {tier.icon} {tier.name}
              </button>
            ))}
          </div>
          {TIER_META.map((tier, i) => (
            <div key={tier.name} className={"tier-card" + (activeTierCard === i ? " visible" : " hidden") + (tier.recommended ? " tier-card-recommended" : "")} role="tabpanel" aria-label={tier.name + " tier"}>
              {tier.recommended && <span className="tier-badge">Recommended</span>}
              <h3 className="tier-card-name">{tier.icon} {tier.name}</h3>
              <p className="tier-card-sub">{tier.sub}</p>
              <ul className="tier-card-features">
                {FEATURE_ROWS.filter((r) => !r.isNote).map((row) => (
                  <li key={row.label} className={"tier-feature-item" + (row.tiers[i] ? " included" : " excluded")}>
                    <span className="tier-feature-icon" aria-hidden="true">{row.tiers[i] ? "✓" : "—"}</span>
                    {row.label}
                  </li>
                ))}
              </ul>
              <div className="tier-card-cost">{FEATURE_ROWS.find((r) => r.isNote)?.tiers[i]}</div>
              <button className="tier-card-cta" onClick={handlePostAJobClick}>Get started →</button>
            </div>
          ))}
        </div>
        <div className="tiers-table-wrap">
          <div className="tiers-table" role="table" aria-label="Job tier comparison">
            <div className="tiers-row tiers-row-head" role="row">
              <div className="tiers-cell tiers-cell-feature" role="columnheader" />
              {TIER_META.map((tier) => (
                <div className={"tiers-cell tiers-cell-head" + (tier.recommended ? " is-recommended" : "")} role="columnheader" key={tier.name}>
                  {tier.recommended && <span className="tiers-recommended-tag">Recommended</span>}
                  <span className="tiers-cell-icon">{tier.icon}</span>
                  <span className="tiers-cell-name">{tier.name}</span>
                  <span className="tiers-cell-sub">{tier.sub}</span>
                </div>
              ))}
            </div>
            {FEATURE_ROWS.map((row) => (
              <div className={"tiers-row" + (row.isNote ? " tiers-row-note" : "")} role="row" key={row.label}>
                <div className="tiers-cell tiers-cell-feature" role="rowheader">{row.label}</div>
                {row.tiers.map((value, i) => (
                  <div className={"tiers-cell" + (TIER_META[i].recommended ? " is-recommended" : "")} role="cell" key={`${row.label}-${TIER_META[i].name}`}>
                    {typeof value === "string" ? (
                      <span className="tiers-note-value">{value}</span>
                    ) : value ? (
                      <span className="tiers-check" aria-label="Included">✓</span>
                    ) : (
                      <span className="tiers-dash" aria-hidden="true">—</span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="tiers-cta">
          <button className="cta-pill-btn" onClick={handlePostAJobClick}>Start with Free — upgrade anytime</button>
        </div>
      </section>

      {/* Steps */}
      <section className="preview-steps" id="how-it-works">
        <div className="steps-header">
          <span className="section-kicker">How it works</span>
          <h2>Post a job in 4 simple steps</h2>
          <p>The whole process takes less than 5 minutes.</p>
        </div>
        <div className="steps-timeline">
          {STEPS.map((step, index) => (
            <div className="step-item" key={step.title}>
              <div className="step-icon">{step.icon}<span className="step-number">{index + 1}</span></div>
              <h4>{step.title}</h4>
              <p>{step.copy}</p>
            </div>
          ))}
        </div>
        <div className="steps-cta">
          <button className="cta-pill-btn cta-pill-dark" onClick={handlePostAJobClick}>Post your first job →</button>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="preview-footer-cta">
        <div className="footer-cta-blob" aria-hidden="true" />
        <div className="footer-cta-inner">
          <div className="footer-cta-badge">Get started today</div>
          <h2>Ready to find your next great hire?</h2>
          <p>Join employers already using Workbrook to hire faster, reach further, and hire right.</p>
          <div className="footer-cta-actions">
            <button className="footer-primary-btn" onClick={handlePostAJobClick}>Post a Job Now — it's free</button>
            {!isAuthenticated && (
              <button className="footer-secondary-btn" onClick={handleSignInClick}>Sign in to your account</button>
            )}
          </div>
          <p className="footer-cta-note">No credit card required · Cancel anytime</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="preview-footer">
        <div className="footer-container">
          <div className="footer-logo">workbrook</div>
          <div className="footer-links">
            <a href="https://workbrook.com/workbrook-privacy-policy/" target="_blank" rel="noreferrer">Privacy Policy</a>
            <a href="https://workbrook.com/apps-term-of-use/" target="_blank" rel="noreferrer">Terms of Use</a>
            <a href="mailto:support@workbrook.com">support@workbrook.com</a>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} Workbrook. All rights reserved.</p>
        </div>
      </footer>

      {/* Lead modal */}
      {showLeadModal && (
        <div className="lead-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="lead-modal-title">
          <div className="lead-modal">
            <button className="lead-modal-close" onClick={() => setShowLeadModal(false)} aria-label="Close">×</button>
            <div className="lead-modal-content">
              <h3 id="lead-modal-title">Join our community of HR leaders</h3>
              <p>Sign up to receive tips and resources to help you hire faster and better.</p>
              {leadSuccess ? (
                <div className="lead-success-msg">Thanks for joining! We will be in touch soon.</div>
              ) : (
                <form onSubmit={handleLeadSubmit} className="lead-modal-form">
                  <div className="input-group">
                    <input type="email" placeholder="you@company.com" value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)} required />
                  </div>
                  {leadEmailError && <div className="error-text" role="alert">{leadEmailError}</div>}
                  <CustomButton text={leadLoading ? "Submitting..." : "Subscribe"} backgroundColor="#3A6ED4" color="#fff" borderColor="transparent" additionalClass="lead-submit-btn" handleClick={handleLeadSubmit} disabled={leadLoading} />
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostAJobPreview;
